import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, DollarSign, TrendingUp, Clock, AlertCircle } from 'lucide-react';
import NCAAService from '../../../components/dashboards/services/ncaaService';

const NCAAFOddsModal = ({ game, onClose }) => {
  const [odds, setOdds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('spread');

  useEffect(() => {
    const fetchOdds = async () => {
      if (!game?.game?.id) {
        setError('No game ID available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const oddsData = await NCAAService.getOdds(game.game.id);
        setOdds(oddsData);
      } catch (err) {
        console.error('Error fetching odds:', err);
        setError('Failed to load odds data');
      } finally {
        setLoading(false);
      }
    };

    fetchOdds();
  }, [game]);

  if (!game) return null;

  const { teams, game: gameInfo } = game;
  const homeTeam = teams?.home;
  const awayTeam = teams?.away;
  const isLive = ['1H', '2H', 'HT', 'OT'].includes(gameInfo?.status?.short);

  const tabs = [
    { id: 'spread', label: 'Spread', icon: TrendingUp },
    { id: 'moneyline', label: 'Money Line', icon: DollarSign },
    { id: 'totals', label: 'Totals', icon: TrendingUp },
    ...(isLive ? [{ id: 'live', label: 'Live', icon: Clock }] : [])
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur-md border-b border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white mb-2">
                  {awayTeam?.name || 'Away Team'} @ {homeTeam?.name || 'Home Team'}
                </h2>
                <div className="flex items-center gap-4 text-sm text-white/60">
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {gameInfo?.date?.date || 'TBD'}
                  </span>
                  {teams?.home?.conference && (
                    <span>{teams.home.conference}</span>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X size={20} className="text-white/60" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-4 py-3 flex items-center justify-center gap-2 transition-colors ${
                  activeTab === tab.id
                    ? 'bg-emerald-500/20 text-emerald-400 border-b-2 border-emerald-400'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                <tab.icon size={16} />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-150px)] p-6">
            {renderOddsContent()}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  // Render odds content based on active tab
  function renderOddsContent() {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-8">
          <AlertCircle className="mx-auto mb-4 text-red-400" size={48} />
          <p className="text-white/60">{error}</p>
        </div>
      );
    }

    if (!odds || !odds.response?.length) {
      return (
        <div className="text-center py-8">
          <DollarSign className="mx-auto mb-4 text-white/40" size={48} />
          <p className="text-white/60">No odds available for this game</p>
          <p className="text-white/40 text-sm mt-2">
            Odds are typically available 1-7 days before the game
          </p>
        </div>
      );
    }

    // Mock data for demonstration - replace with actual odds parsing
    const mockOdds = {
      spread: {
        home: { line: '+7.5', odds: '-110' },
        away: { line: '-7.5', odds: '-110' }
      },
      moneyline: {
        home: { odds: '+250' },
        away: { odds: '-300' }
      },
      totals: {
        over: { line: '52.5', odds: '-110' },
        under: { line: '52.5', odds: '-110' }
      }
    };

    return (
      <div className="space-y-4">
        {activeTab === 'spread' && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider">Point Spread</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/[0.05] rounded-lg p-4 border border-white/10 hover:border-emerald-400/50 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  {awayTeam?.logo && (
                    <img src={awayTeam.logo} alt={awayTeam.name} className="w-6 h-6 object-contain" />
                  )}
                  <p className="text-sm font-medium text-white">{awayTeam?.name}</p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-emerald-400">{mockOdds.spread.away.line}</span>
                  <span className="text-sm text-white/60">{mockOdds.spread.away.odds}</span>
                </div>
              </div>
              <div className="bg-white/[0.05] rounded-lg p-4 border border-white/10 hover:border-emerald-400/50 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  {homeTeam?.logo && (
                    <img src={homeTeam.logo} alt={homeTeam.name} className="w-6 h-6 object-contain" />
                  )}
                  <p className="text-sm font-medium text-white">{homeTeam?.name}</p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-emerald-400">{mockOdds.spread.home.line}</span>
                  <span className="text-sm text-white/60">{mockOdds.spread.home.odds}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'moneyline' && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider">Money Line</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/[0.05] rounded-lg p-4 border border-white/10 hover:border-emerald-400/50 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  {awayTeam?.logo && (
                    <img src={awayTeam.logo} alt={awayTeam.name} className="w-6 h-6 object-contain" />
                  )}
                  <p className="text-sm font-medium text-white">{awayTeam?.name}</p>
                </div>
                <span className="text-xl font-bold text-emerald-400">{mockOdds.moneyline.away.odds}</span>
              </div>
              <div className="bg-white/[0.05] rounded-lg p-4 border border-white/10 hover:border-emerald-400/50 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  {homeTeam?.logo && (
                    <img src={homeTeam.logo} alt={homeTeam.name} className="w-6 h-6 object-contain" />
                  )}
                  <p className="text-sm font-medium text-white">{homeTeam?.name}</p>
                </div>
                <span className="text-xl font-bold text-emerald-400">{mockOdds.moneyline.home.odds}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'totals' && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider">Total Points</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/[0.05] rounded-lg p-4 border border-white/10 hover:border-emerald-400/50 transition-colors">
                <p className="text-sm font-medium text-white mb-2">Over</p>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-emerald-400">{mockOdds.totals.over.line}</span>
                  <span className="text-sm text-white/60">{mockOdds.totals.over.odds}</span>
                </div>
              </div>
              <div className="bg-white/[0.05] rounded-lg p-4 border border-white/10 hover:border-emerald-400/50 transition-colors">
                <p className="text-sm font-medium text-white mb-2">Under</p>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-emerald-400">{mockOdds.totals.under.line}</span>
                  <span className="text-sm text-white/60">{mockOdds.totals.under.odds}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'live' && isLive && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-white/80">Live Betting Markets</span>
            </div>
            <div className="text-center py-8">
              <p className="text-white/60">Live odds updating...</p>
              <p className="text-white/40 text-sm mt-2">Check back for real-time betting opportunities</p>
            </div>
          </div>
        )}
      </div>
    );
  }
};

export default NCAAFOddsModal;